import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox, FlexBoxSpaceBetween } from '@ecp/common/src/layouts/flex/styled';
import Floating, { StyledFloatingWrapper } from '@ecp/common/src/components/floating/bo/Floating';
import { defaultArrowPosition, StyledArrow, StyledArrowInner } from '@ecp/common/src/components/tooltip/Tooltip';
import Tooltip from '@ecp/common/src/components/tooltip/bo/Tooltip';
import { ReactComponent as CollapseArrow } from '@ecp/common/src/assets/icon/collapse_arrow.svg';
import { ReactComponent as QuestionIcon } from '@ecp/common/src/assets/icon/icon__tooltip-question.svg';

const StyledMenu = styled(FlexBoxSpaceBetween)`
  align-items: flex-start;
  align-self: stretch;
  background-color: ${boTheme.color.sub10};
  color: ${boTheme.color.sub1};
  cursor: pointer;
  padding: 0 8px;
  font-size: ${boTheme.font.size.small};
  width: ${({ width }) => `calc(${width} - 2px)`};

  &:hover:not([disabled]) {
    background-color: ${boTheme.color.table4};
  }

  &[disabled] {
    background-color: ${boTheme.color.sub8};
    cursor: not-allowed;
  }
`;

const Menu = ({ item, onClick, menuIcon: MenuIcon, width }) => {
  const { label, disabled } = item;
  const handleClick = () => {
    !disabled && onClick?.(item);
  };

  return (
    <StyledMenu gap={'4px'} width={width} height={'28px'} onClick={handleClick} disabled={disabled}>
      <FlexBox gap={'4px'} align-self="stretch">
        {MenuIcon && <MenuIcon width={'14px'} height={'15px'} />}
        {label}
      </FlexBox>
      {item.help && (
        <Tooltip arrow placement={'bottom-start'} value={item.helpValue} openWhenHovered>
          <QuestionIcon />
        </Tooltip>
      )}
    </StyledMenu>
  );
};

Menu.propTypes = {
  item: PropTypes.object,
  onClick: PropTypes.func,
  menuIcon: PropTypes.elementType,
  width: PropTypes.string,
};

export const flatBottom = css`
  border-radius: 3px 3px 0 0;
  border-bottom: none;
  padding-bottom: 1px;
`;

export const flatTop = css`
  border-radius: 0 0 3px 3px;
  border-top: none;
  padding-top: 1px;
`;

const openCss = css`
  border-color: ${boTheme.color.main};

  &.bottom {
    ${({ isIconType }) => (!isIconType ? flatBottom : '')};
  }

  &.top {
    ${({ isIconType }) => (!isIconType ? flatTop : '')};
  }
`;

export const StyledDropdownMenu = styled(StyledMenu)`
  justify-content: space-between;
  align-items: center;
  background-color: ${boTheme.color.sub10};
  border-width: 1px;
  border-style: solid;
  border-color: ${({ error }) => (error ? boTheme.color.error1 : boTheme.color.sub6)};
  border-radius: 3px;
  font-size: ${boTheme.font.size.small};
  width: ${({ width, isIconType }) => (isIconType ? 'auto' : width)};

  &[disabled] {
    border: 1px solid ${boTheme.color.sub6};
    background-color: ${boTheme.color.sub8};
    cursor: not-allowed;
  }

  ${({ open }) => (open ? openCss : '')}
`;

export const StyledCollapseArrow = styled(CollapseArrow)`
  width: 10px;
  height: 6px;

  & path {
    fill: ${boTheme.color.sub2};
  }

  &[disabled] path {
    fill: ${boTheme.color.sub6};
  }

  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'none')};
`;

export const StyledFloatingRootComponent = styled(StyledFloatingWrapper)`
  border-color: ${boTheme.color.main};
  width: ${({ width }) => (width ? width : 'max-content')};

  &.bottom {
    ${({ isIconType }) => (!isIconType ? flatTop : '')};
  }

  &.top {
    ${({ isIconType }) => (!isIconType ? flatBottom : '')};
  }
`;

const DropdownMenu = ({ menuLabel, items, disabled, onClick, menuIcon: MenuIcon, isIconType, width, error }) => {
  const [open, setOpen] = useState(false);

  const handleClick = (item) => {
    setOpen(false);
    onClick?.(item);
  };

  const handleSetOpen = (isOpen) => {
    setOpen(isOpen);
  };

  const arrowProps = useMemo(() => {
    return isIconType
      ? {
          arrow: true,
          arrowComponent: StyledArrow,
          arrowInnerComponent: StyledArrowInner,
          offset: 6 - defaultArrowPosition,
          arrowPadding: 14,
          placement: 'bottom-start',
          rootProps: { isIconType },
        }
      : {
          placement: 'bottom',
        };
  }, [isIconType]);

  return (
    <Floating
      value={
        <>
          {items.map((item) => (
            <Menu item={item} key={item.key} onClick={handleClick} menuIcon={MenuIcon} width={width} />
          ))}
        </>
      }
      openWhenClicked={!disabled && true}
      closeWhenOutside={!disabled && true}
      open={open}
      onOpenChange={handleSetOpen}
      rootComponent={StyledFloatingRootComponent}
      blockAutoFocus
      {...arrowProps}
    >
      <StyledDropdownMenu
        gap={'4px'}
        width={width}
        height={'28px'}
        open={open}
        disabled={disabled}
        error={error}
        isIconType={isIconType}
      >
        {isIconType ? (
          MenuIcon && <MenuIcon width={'14px'} height={'15px'} />
        ) : (
          <>
            <FlexBox gap={'4px'}>
              {MenuIcon && <MenuIcon width={'14px'} height={'15px'} />}
              {menuLabel}
            </FlexBox>
            <StyledCollapseArrow open={open} />
          </>
        )}
      </StyledDropdownMenu>
    </Floating>
  );
};

DropdownMenu.propTypes = {
  items: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onClick: PropTypes.func,
  menuIcon: PropTypes.elementType,
  menuLabel: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  isIconType: PropTypes.bool,
  width: PropTypes.string,
};

DropdownMenu.defaultProps = {
  width: '140px',
};

export default DropdownMenu;
